<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="signature" :class="[`placement-${placement}`]">
    <svg
      v-if="fields.placeholderEnabled !== false && !hasUserDrawn"
      class="signature__placeholder-layer"
      viewBox="0 0 322 195"
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        :key="animationKey"
        class="signature__placeholder"
        :class="{ 'is-animated': fields.placeholderAnimationEnabled || previewActive }"
        :d="PLACEHOLDER_D"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <svg
      ref="svgEl"
      class="signature__ink-layer"
      viewBox="0 0 322 195"
      preserveAspectRatio="none"
      @touchstart.prevent
      @touchmove.prevent
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <polyline
        v-for="(pts, i) in strokes"
        :key="i"
        class="signature__stroke"
        :points="pts"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <polyline
        v-if="currentStroke"
        class="signature__stroke"
        :points="currentStroke"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <div v-if="labelVisible && fields.label?.key" class="signature__label">
      <TipTap v-model="labelValue" mode="single" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

import type { ColorField, SizeField } from '@render/types/styles'

import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { useTranslation } from '@render/composables/useTranslation'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { PipeDisplayMode, usePipeIfAvailable } from '@render/composables/usePipe'
import TipTap from '@render/lib/TipTap'
import type { SignatureFields as Model, LabelPlacement } from './definition'

type TextDecoration = 'italic' | 'underline' | 'line-through'

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

// Hardcoded from the reference signatureSvg.svg — already in the 322×195
// viewBox coordinate space so no remapping is needed.
const PLACEHOLDER_D =
  'M67.3516 123.651C70.7079 123.348 78.6435 120.911 99.7516 106.253C121.218 91.3469 122.8 76.5153 124.975 69.5681C125.576 67.647 123.149 65.7291 120.855 64.198C116.461 61.2658 109.062 62.0417 105.232 63.1038C97.5915 65.2226 108.087 83.7245 118.184 105.003C127.364 124.35 132.326 138.649 133.549 143.688C135.122 150.169 135.094 155.21 133.724 157.656C131.875 160.957 124.409 162.263 116.754 163.339C113.676 163.771 112.133 162.897 110.593 161.522C102.696 154.474 103.214 137.83 105.802 120.042C107.216 110.327 113.917 103.145 119.568 97.4855C125.643 91.4004 135.959 89.0303 145.595 86.5797C160.657 82.7495 176.391 89.9038 187.32 95.8901C191.164 97.9955 193.623 100.957 195.614 103.403C196.534 104.533 196.694 105.849 196.092 106.474C195.49 107.099 193.973 107.099 192.584 106.644C189.791 105.729 187.821 102.823 186.29 99.7659C184.86 96.9099 185.053 93.628 185.812 90.7222C186.211 89.1913 188.088 88.1199 189.779 87.4946C193.126 86.2578 196.989 87.1728 200.354 88.0877C206.109 89.6521 208.336 95.1268 211.55 97.5958C214.672 99.9948 218.451 94.2257 221.2 94.6809C234.502 96.8829 240.216 119.66 241.899 120.125C243.278 119.072 244.207 117.223 245.122 116.148C245.734 115.982 246.644 116.589 247.582 117.214'

const pipe = usePipeIfAvailable()
const isEditor = computed(() => pipe?.currentMode.value === PipeDisplayMode.EDITOR)

const placement = computed<LabelPlacement>(() => fields.value.labelPlacement ?? 'inside-top')
const labelVisible = computed(() => placement.value !== 'hidden')

const labelValue = useTranslation(() => fields.value.label?.key)

// Drawing state
const svgEl = ref<SVGSVGElement | null>(null)
const strokes = ref<string[]>([])
const currentStroke = ref<string | null>(null)
const hasUserDrawn = ref(false)

function pointToViewBox(e: PointerEvent): [number, number] | null {
  const el = svgEl.value
  if (!el) return null
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null
  const x = ((e.clientX - rect.left) * 322) / rect.width
  const y = ((e.clientY - rect.top) * 195) / rect.height
  return [x, y]
}

function onPointerDown(e: PointerEvent) {
  const p = pointToViewBox(e)
  if (!p) return
  hasUserDrawn.value = true
  svgEl.value?.setPointerCapture(e.pointerId)
  currentStroke.value = `${p[0].toFixed(2)},${p[1].toFixed(2)}`
}

function onPointerMove(e: PointerEvent) {
  if (currentStroke.value === null) return
  const p = pointToViewBox(e)
  if (!p) return
  currentStroke.value = `${currentStroke.value} ${p[0].toFixed(2)},${p[1].toFixed(2)}`
}

function onPointerUp() {
  if (currentStroke.value !== null) {
    strokes.value.push(currentStroke.value)
    currentStroke.value = null
  }
}

function clear() {
  strokes.value = []
  currentStroke.value = null
  hasUserDrawn.value = false
}

// Bumping this on the placeholder path forces a remount so the CSS @keyframes
// animation re-runs (there is no JS-driven step — the class alone drives it).
const animationKey = ref(0)
const previewActive = ref(false)
let previewTimer: ReturnType<typeof setTimeout> | null = null

function previewAnimation() {
  if (previewTimer) {
    clearTimeout(previewTimer)
    previewTimer = null
  }
  clear()
  previewActive.value = true
  animationKey.value++
  const total =
    (fields.value.placeholderAnimationDuration ?? 2000) +
    (fields.value.placeholderAnimationDelay ?? 0)
  previewTimer = setTimeout(() => {
    previewActive.value = false
    previewTimer = null
  }, total + 100)
}

onBeforeUnmount(() => {
  if (previewTimer) clearTimeout(previewTimer)
})

useComponentProcessors({
  clear: () => clear(),
})

useEditorCommands({
  previewAnimation: () => previewAnimation(),
})

// Style bindings — mirror TextInput's `css` computed pattern.
function col(f: ColorField | undefined, fallback: string) {
  return getColorStyleData(f) || fallback
}
function sz(f: SizeField | undefined, fallback: string) {
  return getSizeStyleData(f) || fallback
}

function fontStyleOf(d: TextDecoration[] | undefined): string {
  return d?.includes('italic') ? 'italic' : 'normal'
}
function textDecorationOf(d: TextDecoration[] | undefined): string {
  const parts = (d ?? []).filter((v) => v === 'underline' || v === 'line-through')
  return parts.length ? parts.join(' ') : 'none'
}

const css = computed(() => ({
  width: sz(fields.value.width, '322px'),
  height: sz(fields.value.height, '195px'),
  margin: getSpacingStyleData(fields.value.margin, '0'),
  bg: col(fields.value.bgColor, '#FFFFFF'),
  borderColor: col(fields.value.borderColor, '#85A29E'),
  borderWidth: sz(fields.value.borderWidth, 'var(--border-width)'),
  borderRadius: sz(fields.value.borderRadius, '16px'),
  strokeColor: col(fields.value.strokeColor, '#161616'),
  strokeWidth: `${fields.value.strokeWidth ?? 4}`,
  placeholderColor:
    col(fields.value.placeholderStrokeColor, '') || col(fields.value.strokeColor, '#161616'),
  placeholderOpacity: `${(fields.value.placeholderOpacity ?? 40) / 100}`,
  labelColor: col(fields.value.labelColor, '#0B3E53'),
  labelFontSize: sz(fields.value.labelFontSize, 'var(--text-label-md-size)'),
  labelFontWeight: fields.value.labelFontWeight || 'var(--text-label-md-weight)',
  labelFontStyle: fontStyleOf(fields.value.labelTextStyle),
  labelTextDecoration: textDecorationOf(fields.value.labelTextStyle),
  labelTextAlign: fields.value.labelTextAlign ?? 'center',
  labelEdgeDistance: sz(fields.value.labelEdgeDistance, '20px'),
  labelPointerEvents: isEditor.value ? 'auto' : 'none',
  animationDuration: `${fields.value.placeholderAnimationDuration ?? 2000}ms`,
  animationDelay: `${fields.value.placeholderAnimationDelay ?? 0}ms`,
}))
</script>

<style scoped lang="scss">
.signature {
  position: relative;
  width: v-bind('css.width');
  height: v-bind('css.height');
  margin: v-bind('css.margin');
  background-color: v-bind('css.bg');
  border-color: v-bind('css.borderColor');
  border-style: solid;
  border-width: v-bind('css.borderWidth');
  border-radius: v-bind('css.borderRadius');
  box-sizing: border-box;
  overflow: hidden;
  // iOS Safari ignores `touch-action` on SVG elements, so the ink-layer's own
  // rule isn't enough on iOS — set it on the container HTML element too, and
  // suppress the text-selection magnifier that would otherwise hijack a drag.
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.signature__label {
  position: absolute;
  left: v-bind('css.labelEdgeDistance');
  right: v-bind('css.labelEdgeDistance');
  z-index: 1;
  pointer-events: v-bind('css.labelPointerEvents');
  color: v-bind('css.labelColor');
  font-size: v-bind('css.labelFontSize');
  font-weight: v-bind('css.labelFontWeight');
  font-style: v-bind('css.labelFontStyle');
  text-decoration: v-bind('css.labelTextDecoration');
  text-align: v-bind('css.labelTextAlign');
}

.signature.placement-inside-top .signature__label {
  top: v-bind('css.labelEdgeDistance');
}

.signature.placement-inside-bottom .signature__label {
  bottom: v-bind('css.labelEdgeDistance');
}

.signature__placeholder-layer,
.signature__ink-layer {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.signature__placeholder-layer {
  pointer-events: none;
}

.signature__ink-layer {
  touch-action: none;
  cursor: crosshair;
}

.signature__stroke {
  stroke: v-bind('css.strokeColor');
  stroke-width: v-bind('css.strokeWidth');
}

.signature__placeholder {
  stroke: v-bind('css.placeholderColor');
  stroke-width: v-bind('css.strokeWidth');
  opacity: v-bind('css.placeholderOpacity');

  &.is-animated {
    stroke-dasharray: 2500;
    animation: signature-draw v-bind('css.animationDuration') ease v-bind('css.animationDelay')
      forwards;
  }
}

@keyframes signature-draw {
  from {
    stroke-dashoffset: 2500;
  }
  to {
    stroke-dashoffset: 0;
  }
}
</style>
